// W35 LineChart: Chart.js 4 like legacy `components/charts/LineChart.vue` (same library, so the pixels follow): the
// series colours of `useChartColors`, a $ y axis, grid on y only, a bottom legend of 6 px circles.
import { BarElement, CategoryScale, Chart, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import type { BackofficeScheme } from '@radish/ui-tokens';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Legend, Title, Tooltip);

/**
 * Legacy's charts draw their title inside the canvas (Chart.js `title`: 16 px, the legend's grey, from the left, 30 px over the
 * plot; B1123), so a caller adds no heading of its own. Legacy's canvas is vue-chart-3's 400 x 400 kept square, as wide as its
 * column: as tall as it is wide up to 400 px. A caller's chart box takes these classes.
 */
export const CHART_BOX = 'relative aspect-square max-h-[400px] w-full';

const titleOf = (title: string, dark: boolean) => ({
  display: title !== '', text: title, align: 'start' as const, position: 'top' as const, color: dark ? '#ffffffde' : '#00000070',
  font: { size: 16 }, padding: { top: 0, bottom: 30 },
});

const SERIES: Readonly<Record<BackofficeScheme, readonly string[]>> = {
  light: ['#1e86e3', '#4caf50', '#7e57c2', '#ff9800', '#00bcd4'],
  dark: ['#a6bcee', '#96dda8', '#c59afe', '#ffd497', '#86eaf6'],
};

interface ChartSeries {
  readonly label: string;
  /** Whole dollars and cents as display numbers (chart geometry only, never summed or stored). */
  readonly values: readonly number[];
}

interface LineChartProps {
  readonly labels: readonly string[];
  readonly series: readonly ChartSeries[];
  readonly scheme: BackofficeScheme;
  readonly title: string;
  /**
   * One fixed dataset style instead of the reports' series colours in turn at 2.5 px (B1190): legacy's employee breakdown
   * hands LineChart.vue a blue dataset with no width or radius, so Chart.js's own 3 px line and 3 px points draw it, its
   * legend circle filled in the 25 % blue, in both schemes.
   */
  readonly fixed?: { readonly fill: string; readonly border: string; readonly width: number; readonly radius: number };
}

export function LineChart({ labels, series, scheme, title, fixed }: LineChartProps) {
  const dark = scheme === 'dark';
  const line = dark ? '#373739' : '#cccdcb';
  const tick = dark ? '#ffffffde' : '#00000087';
  const legend = dark ? '#ffffffde' : '#00000070';
  const colors = SERIES[scheme];
  return (
    <figure aria-label={title} className="m-0 h-full">
      <Line
        aria-label={title}
        role="img"
        data={{
          labels: [...labels],
          datasets: series.map((s, i) => ({
            label: s.label,
            data: [...s.values],
            fill: false,
            borderColor: fixed?.border ?? colors[i % colors.length],
            backgroundColor: fixed?.fill ?? `${colors[i % colors.length] ?? '#1e86e3'}90`,
            pointBackgroundColor: fixed?.border ?? colors[i % colors.length],
            borderWidth: fixed?.width ?? 2.5,
            radius: fixed?.radius ?? 2.5,
            pointStyle: 'circle' as const,
          })),
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            x: { border: { color: line }, grid: { display: false }, ticks: { color: tick } },
            y: { border: { color: line }, grid: { color: line }, ticks: { color: tick, callback: (v) => `$${String(v)}` } },
          },
          plugins: {
            title: titleOf(title, dark),
            legend: {
              position: 'bottom',
              align: 'center',
              labels: { color: legend, boxHeight: 6, boxWidth: 6, padding: 20, pointStyle: 'circle', usePointStyle: true },
            },
          },
        }}
      />
    </figure>
  );
}

interface BarChartProps {
  /** One bar per label (legacy "Top 5 … Sales" on a single day). */
  readonly labels: readonly string[];
  /** Dollars, or a count, as display numbers (chart geometry only). */
  readonly values: readonly number[];
  readonly scheme: BackofficeScheme;
  readonly title: string;
  /** What the value axis counts: dollars (legacy's `use-dollar`, `$` ticks) or a plain count. */
  readonly unit?: 'dollars' | 'count';
  /** Legacy's `step-size` prop: the value axis's tick step (default 2; Item Sales and Dining Options 10, Employee Sales 5). */
  readonly stepSize?: number;
  /** The dataset's label, which legacy's tooltip puts before the value ("Gross Sales: $12.50"); legacy leaves some empty. */
  readonly seriesLabel?: string;
  /** One fixed colour for every bar (the employee breakdown's blue) instead of the series colours in turn. */
  readonly color?: { readonly fill: string; readonly border: string };
}

/**
 * Legacy BarChart.vue's `convertToTwoDec` for a `$` tick: a whole value gains ".00", one decimal gains a "0", anything else is
 * rounded to two (tick labels only, never money arithmetic).
 */
export function legacyTickTwoDecimals(value: number | string): string {
  const text = String(value);
  if (!text.includes('.')) return `${text}.00`;
  if (text.split('.')[1]?.length === 1) return `${text}0`;
  return Number.parseFloat(text).toFixed(2);
}

/**
 * Legacy `components/charts/BarChart.vue` with each report's dataset (B1143): always horizontal, no grid on either axis, bars
 * 50 px thick filled in the series colour at alpha 0x90 under a 1.5 px border in the full colour, square-cornered; at most five
 * value ticks, none skipped, every `stepSize`.
 */
export function BarChart({ labels, values, scheme, title, unit = 'dollars', stepSize = 2, seriesLabel = '', color }: BarChartProps) {
  const dark = scheme === 'dark';
  const line = dark ? '#373739' : '#cccdcb';
  const tick = dark ? '#ffffffde' : '#00000087';
  const colors = SERIES[scheme];
  const dollar = unit === 'dollars';
  return (
    <figure aria-label={title} className="m-0 h-full">
      <Bar
        aria-label={title}
        role="img"
        data={{
          labels: [...labels],
          datasets: [{
            label: seriesLabel,
            data: [...values],
            backgroundColor: color === undefined ? labels.map((_, i) => `${colors[i % colors.length] ?? '#1e86e3'}90`) : color.fill,
            borderColor: color === undefined ? labels.map((_, i) => colors[i % colors.length] ?? '#1e86e3') : color.border,
            borderWidth: 1.5,
            barThickness: 50,
            maxBarThickness: 50,
          }],
        }}
        options={{
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            x: {
              border: { color: line },
              grid: { display: false },
              ticks: {
                autoSkip: false, color: tick, maxTicksLimit: 5, stepSize,
                callback: (v: string | number) => (dollar ? `$${legacyTickTwoDecimals(v)}` : v),
              },
            },
            y: { border: { color: line }, grid: { display: false }, ticks: { color: tick } },
          },
          plugins: {
            title: titleOf(title, dark),
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (context: { readonly dataset: { readonly label?: string | undefined }; readonly parsed: { readonly x: number | null } }) => {
                  const named = context.dataset.label ?? '';
                  const prefix = named === '' ? '' : `${named}: `;
                  if (context.parsed.x === null) return prefix;
                  return prefix + (dollar
                    ? `$${context.parsed.x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : String(context.parsed.x));
                },
              },
            },
          },
        }}
      />
    </figure>
  );
}
